import { type DataNeededId, Dtos, type SuretyAccountId } from "@inrev/common";
import { DateTime } from "luxon";
import { useRequest } from "../../../utils/request";
import { dataNeededQueryKey } from "../decisioning.keys";
import { type MutationFailureAction } from "../mutationFailure";
import { replaceById } from "../optimistic";
import { type DataNeeded } from "../types";
import { useOptimisticListMutation } from "../useOptimisticListMutation";

export type UpdateDataNeededInput = {
	id: DataNeededId;
	changes: Dtos.Decisioning.DataNeeded.Update.Request;
};

export const optimisticallyUpdateDataNeeded = (
	item: DataNeeded,
	changes: Dtos.Decisioning.DataNeeded.Update.Request,
): DataNeeded => ({
	...item,
	...changes,
	updatedAt: DateTime.now(),
});

type UpdateDataNeededOptions = {
	/**
	 * Offered with the failure toast, e.g. reopening the sheet with what was typed. Edits apply
	 * as soon as they are saved, so without it a failed edit's changes are lost.
	 */
	failureAction?: (input: UpdateDataNeededInput) => MutationFailureAction | undefined;
};

export const useUpdateDataNeeded = (
	accountId: SuretyAccountId,
	{ failureAction }: UpdateDataNeededOptions = {},
) => {
	const { patch } = useRequest();

	const mutation = useOptimisticListMutation<DataNeeded, UpdateDataNeededInput, DataNeeded>({
		queryKey: dataNeededQueryKey(accountId),
		request: async ({ id, changes }) =>
			Dtos.Decisioning.DataNeeded.Get.Response.schema.parse(
				await patch<unknown>(`/v2/admin/surety/accounts/${accountId}/data-needed/${id}`, changes),
			),
		apply: (items, { id, changes }) =>
			items.map((item) => (item.id === id ? optimisticallyUpdateDataNeeded(item, changes) : item)),
		affectedIds: ({ id }) => [id],
		failureMessage: () => "Couldn't save the request.",
		failureAction,
		reconcile: (items, updated) => replaceById(items, updated.id, updated),
	});

	return {
		updateDataNeeded: mutation.mutate,
		/** Resolves once the server confirms, so a confirmation can wait for it; rejects on failure. */
		updateDataNeededAsync: mutation.mutateAsync,
	};
};
